import {z} from "zod";

const nullableTime = z.string().nullable();
// PostgreSQL and Java UUIDs do not require RFC version/variant bits; legacy history contains such IDs.
const databaseUuidValidator = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, "Invalid UUID");
const nullableUuid = databaseUuidValidator.nullable();

export const taskRowValidator = z.object({
    id: databaseUuidValidator,
    taskId: databaseUuidValidator,
    companyId: nullableUuid,
    dataClusterId: databaseUuidValidator,
    dataClusterName: z.string().nullable(),
    templateName: z.string().nullable(),
    healthStatus: z.string().nullable(),
    dataSourceId: nullableUuid,
    dataSourceName: z.string().nullable(),
    accountName: z.string().nullable(),
    datasets: z.string().nullable(),
    taskType: z.string().nullable(),
    taskState: z.string(),
    cursorType: z.string().nullable(),
    debugging: z.boolean(),
    attemptNumber: z.number().int(),
    taskCreatedAt: nullableTime,
    scheduledAt: nullableTime,
    startedAt: nullableTime,
    heartbeatAt: nullableTime,
    finishedAt: nullableTime,
    recordedAt: nullableTime,
    errorMessage: z.string().nullable(),
    replacedBy: nullableUuid,
    rowsInserted: z.number().nullable(),
    rowsDeleted: z.number().nullable(),
    lastAttemptState: z.string().nullable(),
    lastAttemptError: z.string().nullable(),
    queueBlockReason: z.string().nullable(),
    timing: z.object({
        eligibility: z.string(),
        totalWaitMilliseconds: z.number().nullable(),
        eligibleWaitMilliseconds: z.number().nullable(),
        processingMilliseconds: z.number().nullable(),
        heartbeatAgeMilliseconds: z.number().nullable(),
        staleClaim: z.boolean(),
    }),
});
export const taskPageValidator = z.object({items: z.array(taskRowValidator), total: z.number(), limit: z.number(), offset: z.number(), asOf: z.string()});
const poolValidator = z.object({capacity: z.number(), active: z.number(), available: z.number(), completedSinceStart: z.number()});
export const taskStatusValidator = z.object({
    queued: z.number(),
    due: z.number(),
    scheduled: z.number(),
    inProgress: z.number(),
    staleClaims: z.number(),
    instanceId: z.string(),
    workersAvailable: z.boolean(),
    lightPool: poolValidator.nullable(),
    heavyPool: poolValidator.nullable(),
    pollIntervalMilliseconds: z.number(),
    recoveryIntervalMilliseconds: z.number(),
    asOf: z.string(),
});
export type TaskRow = z.infer<typeof taskRowValidator>;
export type TaskStatus = z.infer<typeof taskStatusValidator>;
export type TaskView = "queue" | "history";
export const ACTIVE_STATES = ["QUEUED", "IN_PROGRESS"];
export const HISTORY_STATES = ["COMPLETED", "FAILED", "SUPERSEDED", "DISCARDED"];

export const EMPTY_FILTERS = {
    companyId: "",
    dataClusterId: "",
    datasetId: "",
    taskId: "",
    state: "",
    taskType: "",
    cursorType: "",
    debugging: "",
    createdFrom: "",
    createdTo: "",
};
export type TaskFilters = typeof EMPTY_FILTERS;
const filterValidator = z
    .object({
        companyId: nullableUuid,
        dataClusterId: nullableUuid,
        datasetId: nullableUuid,
        taskId: nullableUuid,
        state: z.enum(["QUEUED", "IN_PROGRESS", "COMPLETED", "FAILED", "SUPERSEDED", "DISCARDED"]).nullable(),
        taskType: z.enum(["INITIALIZE", "REFRESH", "DELETE"]).nullable(),
        cursorType: z.enum(["INITIAL", "FUTURE", "HISTORICAL"]).nullable(),
        debugging: z.boolean().nullable(),
        createdFrom: z.string().datetime().nullable(),
        createdTo: z.string().datetime().nullable(),
    })
    .refine((value) => value.createdFrom === null || value.createdTo === null || value.createdFrom < value.createdTo, {
        message: "End time must be after start time",
        path: ["createdTo"],
    });

export function parseTaskFilters(filters: TaskFilters) {
    return filterValidator.safeParse({
        ...filters,
        companyId: filters.companyId.trim() || null,
        dataClusterId: filters.dataClusterId.trim() || null,
        datasetId: filters.datasetId.trim() || null,
        taskId: filters.taskId.trim() || null,
        state: filters.state || null,
        taskType: filters.taskType || null,
        cursorType: filters.cursorType || null,
        debugging: filters.debugging === "" ? null : filters.debugging === "true",
        createdFrom: filters.createdFrom === "" ? null : `${filters.createdFrom}:00Z`,
        createdTo: filters.createdTo === "" ? null : `${filters.createdTo}:00Z`,
    });
}

export function formatDuration(milliseconds: number | null): string {
    if (milliseconds === null) {
        return "—";
    }
    if (milliseconds < 1000) {
        return "<1s";
    }
    const seconds = Math.floor(milliseconds / 1000);
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return [days > 0 ? `${days}d` : "", hours > 0 ? `${hours}h` : "", minutes > 0 ? `${minutes}m` : "", `${seconds % 60}s`].filter(Boolean).join(" ");
}

export function formatUtc(value: string | null): string {
    return value === null ? "—" : new Date(value).toISOString();
}

export const taskSearchValidator = z.object({
    view: z.enum(["queue", "history"]).catch("queue").default("queue"),
    companyId: z.string().optional(),
    dataClusterId: z.string().optional(),
    datasetId: z.string().optional(),
    taskId: z.string().optional(),
    state: z.string().optional(),
    taskType: z.string().optional(),
    cursorType: z.string().optional(),
    debugging: z.string().optional(),
    createdFrom: z.string().optional(),
    createdTo: z.string().optional(),
});
export type TaskSearch = {view: TaskView} & Partial<TaskFilters>;
export function taskFiltersFromSearch(search: TaskSearch): TaskFilters {
    return Object.fromEntries(Object.entries(EMPTY_FILTERS).map(([key, fallback]) => [key, search[key as keyof TaskFilters] ?? fallback])) as TaskFilters;
}
